import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DeleteConfirmModal from './DeleteConfirmModal';
import SuccessModal from './SuccessModal';

const API_BASE_URL = 'https://www.testometer.in:8084/api';

const Projects = ({ selectedProjects, availableProjects, onProjectsChange, onAddNewProject, onProjectsRefresh, onEditProject }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedDifficulty, setSelectedDifficulty] = useState('All');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const categories = ['All', ...new Set(availableProjects.map(project => project.category))];
  const difficulties = ['All', ...new Set(availableProjects.map(project => project.difficulty))];

  const filteredProjects = availableProjects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || project.category === selectedCategory;
    const matchesDifficulty = selectedDifficulty === 'All' || project.difficulty === selectedDifficulty;
    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  const sortedProjects = filteredProjects.sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];
    
    if (sortBy === 'name' || sortBy === 'category' || sortBy === 'difficulty') {
      aValue = (aValue || '').toString().toLowerCase();
      bValue = (bValue || '').toString().toLowerCase();
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const handleProjectToggle = (projectId) => {
    const newSelectedProjects = selectedProjects.includes(projectId)
      ? selectedProjects.filter(id => id !== projectId)
      : [...selectedProjects, projectId];
    onProjectsChange(newSelectedProjects);
  };

  const getSelectedProjectNames = () => {
    return availableProjects
      .filter(project => selectedProjects.includes(project.id))
      .map(project => project.name);
  };

  const getDifficultyClass = (difficulty) => {
    switch (difficulty) {
      case 'Beginner': return 'difficulty-beginner';
      case 'Intermediate': return 'difficulty-intermediate';
      case 'Advanced': return 'difficulty-advanced';
      default: return 'difficulty-default';
    }
  };

  const handleAddNew = () => {
    if (onAddNewProject) {
      onAddNewProject();
    }
  };

  const handleEdit = (project) => {
    if (onEditProject) {
      onEditProject(project);
    }
  };

  const handleDelete = async (projectId) => {
    try {
      await axios.delete(`${API_BASE_URL}/master-data/projects/${projectId}`);
      if (onProjectsRefresh) {
        onProjectsRefresh();
      }
      setShowSuccessModal(true);
      setSuccessMessage('Project deleted successfully!');
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const stripHtml = (html) => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  useEffect(() => {
    let timer;
    if (showSuccessModal) {
      timer = setTimeout(() => setShowSuccessModal(false), 3000);
    }
    return () => clearTimeout(timer);
  }, [showSuccessModal]);

  return (
    <div className="form-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2 className="section-title orange">Projects</h2>
        <button
          type="button"
          onClick={handleAddNew}
          className="btn btn-primary"
        >
          + Add New Project
        </button>
      </div>
      
      {/* Search and Filters */}
      <div className="search-filters">
        <div className="search-input">
          <input
            type="text"
            placeholder="Search projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input-field"
          />
        </div>
        <div className="filter-controls">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="input-field"
          >
            {categories.map(category => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
          <select
            value={selectedDifficulty}
            onChange={(e) => setSelectedDifficulty(e.target.value)}
            className="input-field"
          >
            {difficulties.map(difficulty => (
              <option key={difficulty} value={difficulty}>
                {difficulty}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Selected Projects */}
      {selectedProjects.length > 0 && (
        <div className="selected-projects-section">
          <h3 className="selected-projects-title">Selected Projects:</h3>
          <div className="selected-projects-list">
            {getSelectedProjectNames().map(projectName => (
              <span
                key={projectName}
                className="selected-project-tag"
              >
                {projectName}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Projects Table */}
      {sortedProjects.length === 0 ? (
        <div className="no-projects">
          <h3>No projects found</h3>
          <p>No projects match your search criteria.</p>
        </div>
      ) : (
        <div className="table-container">
          <table className="projects-table">
            <thead>
              <tr>
                <th className="checkbox-header">Select</th>
                <th onClick={() => handleSort('name')} className="sortable">
                  Project Name {getSortIcon('name')}
                </th>
                <th>Description</th>
                <th onClick={() => handleSort('category')} className="sortable">
                  Category {getSortIcon('category')}
                </th>
                <th>Client</th>
                <th>Domain</th>
                <th onClick={() => handleSort('difficulty')} className="sortable">
                  Difficulty {getSortIcon('difficulty')}
                </th>
                {/* <th>Technologies</th> */}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedProjects.map(project => (
                <tr key={project.id} className={selectedProjects.includes(project.id) ? 'selected-row' : ''}>
                  <td className="checkbox-cell">
                    <input
                      type="checkbox"
                      checked={selectedProjects.includes(project.id)}
                      onChange={() => handleProjectToggle(project.id)}
                      className="project-checkbox"
                      title={selectedProjects.includes(project.id) ? 'Deselect Project' : 'Select Project'}
                    />
                  </td>
                  <td className="project-name">
                    <strong>{project.name}</strong>
                  </td>
                  <td className="project-description">
                    <div dangerouslySetInnerHTML={{ 
                      __html: project.description.length > 100 
                        ? project.description.substring(0, 100) + '...' 
                        : project.description 
                    }} />
                    {project.responsibilities && (
                      <div className="project-responsibilities">
                        <strong>Responsibilities:</strong>
                        <div dangerouslySetInnerHTML={{ 
                          __html: stripHtml(project.responsibilities).length > 50 
                            ? stripHtml(project.responsibilities).substring(0, 50) + '...' 
                            : stripHtml(project.responsibilities)
                        }} />
                      </div>
                    )}
                  </td>
                  <td>
                    <span className="project-category">{project.category}</span>
                  </td>
                  <td>
                    <span className="project-client">{project.client || '-'}</span>
                  </td>
                  <td>
                    <span className="project-domain">{project.domain || '-'}</span>
                  </td>
                  <td>
                    <span className={`project-difficulty ${getDifficultyClass(project.difficulty)}`}>
                      {project.difficulty}
                    </span>
                  </td>
                  {/* <td className="project-technologies">
                    {project.technologies ? (
                      <span>{project.technologies}</span>
                    ) : (
                      <span className="no-technologies">-</span>
                    )}
                  </td> */}
                  <td className="action-cell">
                    <div className="action-buttons">
                      <button
                        type="button"
                        onClick={() => handleEdit(project)}
                        className="btn btn-small btn-secondary"
                        title="Edit Project"
                      >
                        ✏️
                      </button>
                      <button
                        type="button"
                        onClick={() => { setProjectToDelete(project.id); setShowDeleteModal(true); }}
                        className="btn btn-small btn-danger"
                        title="Delete Project"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Project Count */}
      <div className="project-stats">
        <span className="stats-text">
          Total Projects: {availableProjects.length}
        </span>
        {(searchTerm || selectedCategory !== 'All' || selectedDifficulty !== 'All') && (
          <span className="stats-text">
            Filtered: {sortedProjects.length}
          </span>
        )}
        <span className="stats-text">
          Selected: {selectedProjects.length}
        </span>
      </div>

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => { handleDelete(projectToDelete); setProjectToDelete(null); }}
        message="Are you sure you want to delete this project? This action cannot be undone."
        title="Delete Project"
      />

      <SuccessModal isOpen={showSuccessModal} onClose={() => setShowSuccessModal(false)} message={successMessage} />
    </div>
  );
};

export default Projects; 